import React, { useState } from 'react';
import { first } from 'lodash';

import { ScaleLinear } from 'd3-scale';

import { Bar, Line } from '@visx/shape';
import { GridColumns, GridRows } from '@visx/grid';
import { AxisLeft, AxisRight } from '@visx/axis';
import { LinearGradient } from '@visx/gradient';
import { getStringWidth } from '@visx/text';
import { localPoint } from '@visx/event';
import { common, model } from '@gosupersimple/types';

import classNames from 'classnames';

import { notNil } from '@/lib/utils';
import { formatPropertyValue } from '@/explore/utils/format';
import { ChartType } from '@/explore/types';
import { GroupFilter } from '@/explore/utils/drilldown';

import { ChartTooltip, ChartTooltipItem, detectHoveredSeries, renderGridColumns } from '../common';
import { getScreenPoint, increaseBrightness, sortTooltipData } from '../utils';
import { CategoryData, SeriesMetaData } from '../grouped-chart/types';

import {
  explodeCombinedKey,
  getLeftRightAxisFormats,
  getLeftRightAxisTypes,
  isCombinedKey,
} from '../grouped-chart/utils';

import { SeriesValueDot } from '../timeseries-chart/series-value-dot';
import { BarSeries } from './bar-series';
import { AreaSeries } from './area-series';
import { SeriesValues } from './series-values';
import { LineSeries } from './line-series';

import { NonOverlappingValueLabels } from '../non-overlapping-value-labels';
import { convertTicksToScale } from '../grouped-chart/utils/scale';

import { CategoryScale, CategoryValue } from './types';

import commonStyles, {
  gridLineColor,
  gridHighlightedLineColor,
  hoverLineColor,
  graphVerticalPadding,
} from '../charts.module.scss';

export const NumYTicks = 7;
const GraphVerticalPadding = parseInt(graphVerticalPadding);

export interface ChartColor {
  fill: string;
  highlight: string;
}

interface VerticalCategoryChartProps {
  width: number;
  height: number;
  data: CategoryData;
  categories: CategoryValue[];
  categoryScale: CategoryScale;
  valueScales: {
    left: ScaleLinear<number, number>;
    right: ScaleLinear<number, number>;
  };
  highlightedCategory: CategoryValue | null;
  groupKey: string | undefined;
  onCategoryHighlight: (categoryValue: CategoryValue | null) => void;
  onCategoryClick?: (params: {
    x: number;
    y: number;
    key: string;
    groupFilters: GroupFilter[];
  }) => void;
  hideGrid: boolean;
  timezone: string;
}

export const VerticalCategoryChart = (props: VerticalCategoryChartProps) => {
  const {
    width,
    height,
    data,
    categories,
    categoryScale,
    valueScales,
    highlightedCategory,
    groupKey,
    onCategoryClick,
    onCategoryHighlight,
    hideGrid,
    timezone,
  } = props;

  const hasOnlyBars = data.series.every(({ chartType }) => chartType === 'bar');
  const hasBars = hasOnlyBars || data.series.some(({ chartType }) => chartType === 'bar');

  const [tooltipData, setTooltipData] = useState<TooltipData | null>(null);

  const axisTypes = getLeftRightAxisTypes(data.series);
  const axisFormats = getLeftRightAxisFormats(data.series);

  const formatLeftAxisValue = (n: number) =>
    formatPropertyValue(Math.round(n * 100) / 100, {
      type: axisTypes.left,
      format: axisFormats.left,
      style: 'compact',
    });

  const formatRightAxisValue = (n: number) =>
    formatPropertyValue(Math.round(n * 100) / 100, {
      type: axisTypes.right,
      format: axisFormats.right,
      style: 'compact',
    });

  const yAxisWidths = {
    left: Math.max(
      ...valueScales.left
        .ticks(NumYTicks)
        .map((i) => formatLeftAxisValue(i.valueOf()))
        .map((i) => getStringWidth(i) ?? 0),
    ),
  };

  const renderBarSeriesItem = (
    seriesItem: SeriesMetaData,
    barSeriesIdx: number,
    numBarSeries: number,
  ) => {
    const seriesData = data.items.map((item) => ({
      categoryValue: item.categoryValue,
      value: item.values[seriesItem.key],
    }));

    const { barWidth, x } = getBarSeriesPosition(categoryScale, numBarSeries, barSeriesIdx);

    const hasSomeSeriesHovered = tooltipData?.hoveredSeriesKey !== undefined;

    return (
      <g
        key={`${seriesItem.key}-${barSeriesIdx}`}
        className={classNames(
          isHovered(seriesItem.key) ? commonStyles.seriesHighlighted : commonStyles.seriesFaded,
        )}>
        <BarSeries
          data={seriesData}
          fillColor={seriesItem.color}
          fillOpacity={hasSomeSeriesHovered && !isHovered(seriesItem.key) ? 0.3 : 0.9}
          valueScale={valueScales[seriesItem.axis]}
          categoryScale={categoryScale}
          barWidth={barWidth}
          x={x}
        />
      </g>
    );
  };

  const renderLineSeriesItem = (seriesItem: SeriesMetaData) => {
    const seriesData = data.items.map((item) => ({
      categoryValue: item.categoryValue,
      value: item.values[seriesItem.key],
    }));

    const isHovered = tooltipData?.hoveredSeriesKey === seriesItem.key;
    const hasSomeSeriesHovered = tooltipData?.hoveredSeriesKey !== undefined;

    return (
      <LineSeries
        key={seriesItem.key}
        data={seriesData}
        strokeColor={seriesItem.color}
        opacity={hasSomeSeriesHovered && !isHovered ? 0.3 : 1}
        valueScale={valueScales[seriesItem.axis]}
        categoryScale={categoryScale}
      />
    );
  };

  const renderSeriesValueDots = () => {
    // Only render when there is a single date value
    if (data.items.length !== 1) {
      return null;
    }
    // Only render dots for line and area series
    const series = data.series.filter(
      ({ chartType }) => chartType === 'line' || chartType === 'area',
    );
    return series.map(({ key, axis, color }) =>
      data.items.map((item) => (
        <SeriesValueDot
          key={key}
          cx={(categoryScale(item.categoryValue.formatted) ?? 0) + categoryScale.bandwidth() / 2}
          cy={valueScales[axis](item.values[key] ?? 0)}
          color={color}
        />
      )),
    );
  };

  const renderAreaSeriesItem = (series: SeriesMetaData) => {
    const seriesData = data.items.map((item) => ({
      categoryValue: item.categoryValue,
      value: item.values[series.key],
    }));

    const isHovered = tooltipData?.hoveredSeriesKey === series.key;
    const hasSomeSeriesHovered = tooltipData?.hoveredSeriesKey !== undefined;

    return (
      <AreaSeries
        key={series.key}
        data={seriesData}
        fillColor={series.color}
        strokeColor={increaseBrightness(series.color, 20)}
        opacity={hasSomeSeriesHovered && !isHovered ? 0.3 : 1}
        valueScale={valueScales[series.axis]}
        categoryScale={categoryScale}
      />
    );
  };

  const renderSeries = () => {
    let barSeriesIdx = 0;
    const numBarSeries = data.series.filter(({ chartType }) => chartType === 'bar').length;
    return data.series.map((series) => {
      switch (series.chartType) {
        case 'bar':
          return renderBarSeriesItem(series, barSeriesIdx++, numBarSeries);
        case 'line':
          return renderLineSeriesItem(series);
        case 'area':
          return renderAreaSeriesItem(series);
        default:
          return null;
      }
    });
  };

  const renderSeriesValues = () => {
    const numBarSeries = data.series.filter(({ chartType }) => chartType === 'bar').length;
    let barSeriesIdx = 0;
    return (
      <NonOverlappingValueLabels>
        {data.series.map((series) => {
          if (!series.showValues) {
            barSeriesIdx++;
            return null;
          }
          const seriesData = data.items.map((item) => ({
            categoryValue: item.categoryValue,
            value: item.values[series.key],
          }));

          switch (series.chartType) {
            case 'bar': {
              const { barWidth, x } = getBarSeriesPosition(
                categoryScale,
                numBarSeries,
                barSeriesIdx++,
              );
              return (
                <SeriesValues
                  key={series.key}
                  series={series}
                  data={seriesData}
                  valueScale={valueScales[series.axis]}
                  categoryScale={categoryScale}
                  x={x - categoryScale.bandwidth() / 2 + barWidth / 2}
                />
              );
            }
            case 'line':
            case 'area':
              return (
                <SeriesValues
                  key={series.key}
                  series={series}
                  data={seriesData}
                  valueScale={valueScales[series.axis]}
                  categoryScale={categoryScale}
                />
              );
            default:
              return null;
          }
        })}
      </NonOverlappingValueLabels>
    );
  };

  const renderHighlightedCategory = () => {
    if (!highlightedCategory) {
      return null;
    }

    return (
      <rect
        x={
          (categoryScale(highlightedCategory.formatted) ?? 0) -
          (categoryScale.paddingInner() * categoryScale.step()) / 2
        }
        y={0}
        width={categoryScale.step()}
        height={height}
        fill={gridLineColor}
        pointerEvents="none"
      />
    );
  };

  const handleMouseMove = (
    event: React.TouchEvent<SVGRectElement> | React.MouseEvent<SVGRectElement>,
  ) => {
    const point = localPoint(event);
    if (point === null) {
      setTooltipData(null);
      onCategoryHighlight(null);
      return;
    }

    // Find the category and corresponding data item
    const step = categoryScale.step();
    const paddingOffset = categoryScale.paddingOuter() * categoryScale.step();
    const index = Math.round((point.x - paddingOffset * 2) / step);
    const categoryValue = categories.find((c) => c.formatted === categoryScale.domain()[index]);
    if (categoryValue === undefined) {
      setTooltipData(null);
      onCategoryHighlight(null);
      return;
    }
    const dataItem = data.items.find(
      (item) => item.categoryValue.formatted === categoryValue.formatted,
    );
    if (dataItem === undefined) {
      setTooltipData(null);
      onCategoryHighlight(null);
      return;
    }

    const seriesData = data.series.map((s) => ({
      key: s.key,
      value: dataItem.values[s.key] ?? null,
      chartType: s.chartType,
      axis: s.axis,
    }));

    // Calculate bar positions for bar detection
    let hoveredSeriesKey: string | undefined = undefined;
    if (hasBars) {
      const barSeries = seriesData.filter((s) => s.chartType === 'bar');
      const categoryX = categoryScale(categoryValue.formatted) ?? 0;
      const barPositions = barSeries.map((barData, idx) => {
        const { barWidth, x } = getBarSeriesPosition(categoryScale, barSeries.length, idx);
        return {
          key: barData.key,
          x: categoryX + x,
          width: barWidth,
        };
      });

      // Detect which series is being hovered based on mouse Y position
      hoveredSeriesKey = detectHoveredSeries(
        point.y,
        seriesData,
        valueScales,
        point.x,
        barPositions,
        false,
      );
    }

    setTooltipData(
      buildTooltipData(
        categoryValue,
        getScreenPoint(point, event.target as SVGElement),
        data,
        timezone,
        hoveredSeriesKey,
      ),
    );

    onCategoryHighlight(categoryValue);
  };

  const handleMouseLeave = () => {
    setTooltipData(null);
    onCategoryHighlight(null);
  };

  const domainSizes = {
    left: valueScales.left.domain()[1] - valueScales.left.domain()[0],
    right: valueScales.right.domain()[1] - valueScales.right.domain()[0],
  };

  const hasRightAxisSeries = data.series.some((series) => series.axis === 'right');

  // Default to right as main axis in case no series on left axis
  const primaryValueScale = domainSizes.left > 0 ? valueScales.left : valueScales.right;

  const isHovered = (seriesKey: string) => tooltipData?.hoveredSeriesKey === seriesKey;

  // Force right axis ticks to align with primary axis ticks
  const rightAxisTicks = convertTicksToScale(
    primaryValueScale.ticks(NumYTicks),
    primaryValueScale,
    valueScales.right,
  );

  const graphHeight = height - GraphVerticalPadding;

  return (
    <div className={commonStyles.graph}>
      <svg width={width} height={graphHeight} className={commonStyles.graphMain}>
        {!hideGrid && (
          <>
            <GridRows
              width={width}
              height={height}
              scale={primaryValueScale}
              tickValues={primaryValueScale.ticks(NumYTicks)}
              stroke={gridLineColor}
            />
            {hasOnlyBars ? null : (
              <>
                <GridColumns
                  width={width}
                  height={height}
                  scale={categoryScale}
                  stroke={gridLineColor}
                  // eslint-disable-next-line react/no-children-prop
                  children={(lines) =>
                    renderGridColumns(lines, { width, strokeColor: gridLineColor })
                  }
                />
                <GridColumns
                  width={width}
                  height={height}
                  scale={categoryScale}
                  stroke={gridHighlightedLineColor}
                  // eslint-disable-next-line react/no-children-prop
                  children={(lines) =>
                    renderGridColumns(lines, { width, strokeColor: gridHighlightedLineColor })
                  }
                />
              </>
            )}
          </>
        )}
        {data.series.map(({ key, color }) => (
          <LinearGradient
            key={key}
            id={`gradient-${color}`}
            from={color}
            to={color}
            fromOpacity={0.8}
            toOpacity={0.2}
          />
        ))}
        {renderSeries()}
        {renderSeriesValueDots()}
        {renderSeriesValues()}
        {hideGrid ? null : (
          <>
            {domainSizes.left > 0 && (
              <AxisLeft
                left={yAxisWidths.left + 4}
                scale={valueScales.left}
                hideTicks
                hideAxisLine
                tickClassName={commonStyles.tickLabel}
                tickLabelProps={() => ({ dx: 4, dy: 10, textAnchor: 'end' })}
                tickValues={valueScales.left.ticks(NumYTicks)}
                tickFormat={(n) => formatLeftAxisValue(n.valueOf())}
              />
            )}
            {domainSizes.right > 0 && hasRightAxisSeries && (
              <AxisRight
                left={width - 4}
                scale={valueScales.right}
                hideTicks
                hideAxisLine
                tickClassName={commonStyles.tickLabel}
                tickLabelProps={() => ({ dx: 0, dy: 10, x: 0, textAnchor: 'end' })}
                tickValues={rightAxisTicks}
                tickFormat={(n) => formatRightAxisValue(n.valueOf())}
              />
            )}
          </>
        )}
      </svg>

      <svg width={width} height={graphHeight} className={commonStyles.graphInteractive}>
        {hasBars && renderHighlightedCategory()}
        {tooltipData && (
          <>
            {hasOnlyBars ? null : (
              <Line
                from={{
                  x:
                    (categoryScale(tooltipData.categoryValue.formatted) ?? 0) +
                    categoryScale.bandwidth() / 2,
                  y: 0,
                }}
                to={{
                  x:
                    (categoryScale(tooltipData.categoryValue.formatted) ?? 0) +
                    categoryScale.bandwidth() / 2,
                  y: height,
                }}
                stroke={hoverLineColor}
                strokeWidth={1}
                pointerEvents="none"
                strokeDasharray="5,2"
              />
            )}
            {tooltipData.points.map((point, i) => {
              if (point.value === null || point.chartType === 'bar') {
                return null;
              }

              return (
                <circle
                  key={i}
                  cx={
                    (categoryScale(tooltipData.categoryValue.formatted) ?? 0) +
                    categoryScale.bandwidth() / 2
                  }
                  cy={valueScales[point.axis](point.value ?? 0)}
                  r={5}
                  fill={point.color}
                  stroke="white"
                  strokeOpacity={0.2}
                  strokeWidth={1}
                  pointerEvents="none"
                />
              );
            })}
          </>
        )}

        <Bar
          x={0}
          y={0}
          width={width}
          height={height}
          fill={'transparent'}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          onClick={(e) => {
            if (tooltipData === null || groupKey === undefined) {
              return;
            }
            const { clientX, clientY } = e;
            const series = data.series[0];
            onCategoryClick?.({
              x: clientX,
              y: clientY,
              key: isCombinedKey(series.key) ? explodeCombinedKey(series.key).valueKey : series.key,
              groupFilters: [
                {
                  key: groupKey,
                  value: tooltipData.categoryValue.raw,
                },
              ],
            });
          }}
        />
      </svg>

      {tooltipData && <Tooltip tooltipData={tooltipData} />}
    </div>
  );
};

interface TooltipData {
  categoryLabel: string;
  categoryValue: CategoryValue;
  position: { x: number; y: number };
  timezone: string;
  hoveredSeriesKey?: string;
  points: {
    label: string;
    value: number | null;
    type: model.PropertyType | null;
    format?: common.PropertyValueFormat;
    color: string;
    axis: 'left' | 'right';
    chartType: ChartType;
    isHovered?: boolean;
  }[];
}

const Tooltip = (props: { tooltipData: TooltipData }) => {
  const { tooltipData } = props;

  if (tooltipData.points.length === 0) {
    return null;
  }

  const totalValueType = first(tooltipData.points)?.type ?? 'Number';
  const totalValueFormat = tooltipData.points.every(
    (point) => point.format === first(tooltipData.points)?.format,
  )
    ? first(tooltipData.points)?.format
    : undefined;

  return (
    <ChartTooltip
      position={tooltipData.position}
      title={`${tooltipData.categoryLabel} ${tooltipData.categoryValue.formatted}`}>
      <ul>
        {tooltipData.points.length > 1 && (
          <li>
            Total:{' '}
            <strong>
              {formatPropertyValue(
                tooltipData.points
                  .map((point) => point.value)
                  .filter(notNil)
                  .reduce((sum, value) => sum + value, 0),
                { type: totalValueType, format: totalValueFormat },
              )}
            </strong>
          </li>
        )}
        {sortTooltipData(tooltipData).points.map((point, i) => (
          <ChartTooltipItem
            key={i}
            label={point.label ?? '-'}
            value={formatPropertyValue(point.value, { type: point.type, format: point.format })}
            color={point.color}
            isHovered={point.isHovered}
          />
        ))}
      </ul>
    </ChartTooltip>
  );
};

const buildTooltipData = (
  categoryValue: CategoryValue,
  position: { x: number; y: number },
  data: CategoryData,
  timezone: string,
  hoveredSeriesKey?: string,
): TooltipData | null => {
  const item = data.items.find((item) => item.categoryValue.formatted === categoryValue.formatted);

  if (item === undefined) {
    return null;
  }

  const points = data.series.map(({ key, label, type, format, axis, chartType, color }) => ({
    label,
    value: item.values[key] ?? null,
    type,
    format,
    category: item.categoryValue,
    color,
    axis,
    chartType,
    isHovered: hoveredSeriesKey === key,
  }));

  return {
    categoryLabel: item.label,
    categoryValue,
    position,
    timezone,
    points,
    hoveredSeriesKey,
  };
};

export type HighlightedData = {
  categoryValue: CategoryValue;
  group: string | undefined;
  axis: 'left' | 'right' | undefined;
  seriesKey?: string;
  chartType?: 'stacked' | 'grouped';
  point: {
    x: number;
    y: number;
  };
};

const BarMargin = 0.2;

export const getBarSeriesPosition = (
  categoryScale: CategoryScale,
  numSeries: number,
  seriesIdx: number,
) => {
  const bandwidth = categoryScale.bandwidth();

  if (numSeries > 1) {
    const barWidth = (bandwidth / numSeries) * (1 - BarMargin);
    const barGap = (bandwidth - numSeries * barWidth) / (numSeries - 1);
    return {
      barWidth,
      x: seriesIdx * (barWidth + barGap),
    };
  }

  return {
    barWidth: bandwidth,
    x: 0,
  };
};
